import { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '../../stores/auth-store';
import { useRBACStore, RBACError } from '../../stores/rbac-store';

/**
 * Enhanced RBAC Initialization Hook
 * 
 * This critical hook manages the complete RBAC system initialization lifecycle:
 * - Monitors authentication state changes
 * - Initializes RBAC permissions based on user attributes
 * - Provides intelligent retry mechanisms for failed initializations
 * - Handles graceful error recovery with user-friendly messages
 * - Ensures proper cleanup when users log out
 * 
 * Key Features:
 * - Automatic initialization on login/auth changes
 * - Exponential backoff retry strategy
 * - Comprehensive error categorization and handling
 * - Session-aware initialization to prevent duplicate calls
 * - Performance monitoring for initialization times
 * - Fallback modes for critical system failures
 */
export const useRBACInitialization = () => {
  const { user, session, isLoading: authLoading } = useAuthStore();
  const { 
    initializeRBAC, 
    clearRBAC, 
    retryInitialization,
    getUserRole,
    error: rbacError,
    retryCount,
    maxRetries,
    resetError
  } = useRBACStore();
  
  const [isRetrying, setIsRetrying] = useState(false);

  // Enhanced error handling with categorized, user-friendly messages
  // Provides specific guidance based on error type for better UX
  const getErrorMessage = (error: RBACError | null): string => {
    if (!error) return '';
    
    switch (error.code) {
      case 'NO_ROLE':
        return 'Your account is missing required role information. Please contact your administrator.';
      case 'INITIALIZATION_ERROR':
        return 'Failed to initialize user permissions. Please try refreshing the page.';
      case 'NETWORK_ERROR':
        return 'Network error while loading permissions. Please check your connection.';
      case 'INVALID_PERMISSION':
        return 'Invalid permission configuration. Please contact support.';
      default:
        return error.message || 'An unexpected error occurred while loading permissions.';
    }
  };

  // Retry initialization with exponential backoff
  const handleRetry = useCallback(async () => {
    if (retryCount >= maxRetries) {
      console.error('❌ RBAC Init: Max retries exceeded');
      return;
    }

    setIsRetrying(true);
    try {
      await retryInitialization();
    } catch (error) {
      console.log('[useRBACInitialization][handleRetry] Error:', error)
    } finally {
      setIsRetrying(false);
    }
  }, [retryCount, maxRetries, retryInitialization]);

  // Clear error and retry
  const handleClearError = useCallback(() => {
    resetError();
  }, [resetError]);

  useEffect(() => {
    // Only log when there are errors or important state changes
    if (rbacError) {
      console.warn('⚠️ RBAC Init: Error detected', {
        errorCode: rbacError?.code,
        retryCount
      });
    }

    // If user is authenticated and we have session, initialize RBAC
    if (user && session && !authLoading) {
      initializeRBAC();
    } else if (!user && !session && !authLoading) {
      // If user is not authenticated, clear RBAC data
      clearRBAC();
    }
  }, [user, session, authLoading, initializeRBAC, clearRBAC, getUserRole, rbacError, retryCount]);

  // Return enhanced RBAC state for components that need it
  return {
    isAuthenticated: !!user && !!session,
    authLoading,
    userRole: getUserRole(),
    hasInitialized: !!getUserRole(),
    error: rbacError,
    errorMessage: getErrorMessage(rbacError),
    retryCount,
    maxRetries,
    isRetrying,
    canRetry: retryCount < maxRetries,
    retry: handleRetry,
    clearError: handleClearError
  };
}; 